import { Drawer, Form } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/shallow';

import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useValidateExperienceBulkEdit } from 'app/hooks/data/core/extranet/experience/useExperienceBulkEdit';
import { useEmojis } from 'app/hooks/data/useEmojis';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type { DayOfWeek } from 'app/typings/core/common/enum/dayOfWeek';
import { DAY_OF_WEEK } from 'app/typings/core/common/enum/dayOfWeek';
import { BULK_EDIT_DEFAULT_DAYS } from 'app/typings/core/extranet/constants/days';
import type {
  ExperienceBulkEditFormValues,
  ExperienceBulkEditInitialValues,
  ExperienceBulkEditPayload,
} from 'app/typings/core/extranet/experiences/experienceBulkEdit';
import { STANDARD_DATE_FORMAT } from 'app/utils/dates';
import type { Interval } from 'app/utils/dayjs';
import { format } from 'app/utils/dayjs';

import { inventoryStore } from '../../../../InventoryStore';
import { ItemPreview } from '../../../Common/ItemPreview/ItemPreview';
import { formatExperienceName } from '../../tabViewUtils';
import { ValidationExperienceBulkEditModal } from '../ValidationExperienceBulkEditModal/ValidationExperienceBulkEditModal';
import { getMustHavePackNames } from '../_utils/getMustHavePackNames';

import styles from './ExperienceBulkEdit.module.scss';
import { DatesSelector } from './_components/DatesSelector';
import { ExperienceBulkEditFooter } from './_components/ExperienceBulkEditFooter';
import { ExperienceBulkEditValues } from './_components/ExperienceBulkEditValues';

export const RULES = [{ required: true, message: 'Required' }];

type Props = {
  initialValues: ExperienceBulkEditInitialValues;
  currentRange?: Interval;
  hotelId: number;
};

export const ExperienceBulkEdit = ({
  initialValues,
  hotelId,
  currentRange,
}: Props) => {
  const [form] = Form.useForm();
  const { experiencesList, getExperience, packsList } =
    useContext(ModernHotelContext);

  const formValues = Form.useWatch<ExperienceBulkEditFormValues>([], form);

  const {
    data,
    mutateAsync: validateExperienceBulkEdit,
    isLoading,
  } = useValidateExperienceBulkEdit(hotelId);
  const { data: emojis } = useEmojis();
  const { data: experiencesCategories } = useExperiencesCategories();

  const { setIsBulkEditOpen, isBulkEditDrawerOpen } = useStore(
    inventoryStore,
    useShallow((state) => ({
      isBulkEditDrawerOpen: state.experienceBulkEditDrawer,
      setIsBulkEditOpen: state.setExperienceBulkEditDrawer,
    }))
  );

  const experience = getExperience(isBulkEditDrawerOpen.expId);

  const mustHavePackNames = useMemo(
    () => getMustHavePackNames(experience, packsList),
    [experience, packsList]
  );

  const getClosedDaysFromExperience = useCallback(
    (experienceId?: number): number[] => {
      if (!experienceId || !experiencesList) {
        return [];
      }

      // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
      const experience = experiencesList.find((exp) => exp.id === experienceId);
      const stockConfig = experience?.openingConfiguration?.stockConfiguration;

      if (
        !stockConfig ||
        stockConfig.type !== OPENING_CONFIGURATION_TYPE.DAILY
      ) {
        return [];
      }

      const dayMapping: Record<DayOfWeek, number> = {
        [DAY_OF_WEEK.MONDAY]: 1,
        [DAY_OF_WEEK.TUESDAY]: 2,
        [DAY_OF_WEEK.WEDNESDAY]: 3,
        [DAY_OF_WEEK.THURSDAY]: 4,
        [DAY_OF_WEEK.FRIDAY]: 5,
        [DAY_OF_WEEK.SATURDAY]: 6,
        [DAY_OF_WEEK.SUNDAY]: 7,
      };

      return (Object.keys(dayMapping) as DayOfWeek[])
        .filter((day) => {
          const dayStock = stockConfig.days[day];

          return dayStock?.isClosed ?? false;
        })
        .map((day) => dayMapping[day]);
    },
    [experiencesList]
  );

  const closedDays = useMemo(
    () => getClosedDaysFromExperience(experience?.id),
    [experience?.id, getClosedDaysFromExperience]
  );

  const [isValidationBulkEditModalOpen, setIsValidationBulkEditModalOpen] =
    useState<boolean>(false);

  const [payload, setPayload] = useState<ExperienceBulkEditPayload>({
    experienceId: experience?.id ?? 0,
    startDate: format(
      formValues?.startDate ? new Date(formValues.startDate) : new Date(),
      STANDARD_DATE_FORMAT
    ),
    endDate: format(
      formValues?.endDate ? new Date(formValues.endDate) : new Date(),
      STANDARD_DATE_FORMAT
    ),
    days: formValues?.days ?? [],
    stock: formValues?.stock,
  });

  const onFinish = async (values: ExperienceBulkEditFormValues) => {
    if (!experience?.id) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    const payload: ExperienceBulkEditPayload = {
      experienceId: experience?.id,
      startDate: format(new Date(values.startDate), STANDARD_DATE_FORMAT),
      endDate: format(new Date(values.endDate), STANDARD_DATE_FORMAT),
      days: values.days,
      stock: values.stock,
    };

    setPayload(payload);

    await validateExperienceBulkEdit(payload);
    setIsValidationBulkEditModalOpen(true);
  };

  const handleClose = () => {
    setIsBulkEditOpen(undefined, false);
    form.resetFields();
    setIsValidationBulkEditModalOpen(false);
  };

  const emoji = emojis?.find((e) => e.id === experience?.emoji?.id);
  const categoryName = experiencesCategories?.find(
    (cat) => cat.id === experience?.categoryId
  )?.name;

  const experienceName = experience
    ? formatExperienceName(experience, categoryName)
    : 'Unamed experience';

  const filteredInitialValues = useMemo(
    () => ({
      ...initialValues,
      days: (initialValues.days ?? BULK_EDIT_DEFAULT_DAYS).filter(
        (day) => !closedDays.includes(day)
      ),
    }),
    [initialValues, closedDays]
  );

  useEffect(() => {
    form.setFieldsValue(filteredInitialValues);
  }, [filteredInitialValues, form]);

  return (
    <Drawer
      title={
        <div className={styles.bulkEditHeader}>
          <h1>Bulk Edit</h1>
        </div>
      }
      className={styles.bulkEdit}
      width="510px"
      closable={false}
      open={isBulkEditDrawerOpen.isOpened}
      bodyStyle={{ padding: 0 }}
      onClose={handleClose}
      destroyOnClose
    >
      <Form
        name="bulkEditForm"
        autoComplete="off"
        className={styles.form}
        form={form}
        onFinish={onFinish}
        initialValues={filteredInitialValues}
      >
        <div className={styles.bulkEditBody}>
          <ItemPreview
            title="ACTIVITY"
            name={experienceName}
            path={emoji?.pictureId}
            alt={emoji?.alt}
          />
          <DatesSelector
            fromDate={new Date(formValues?.startDate)}
            startDate={
              initialValues.startDate
                ? new Date(initialValues.startDate)
                : undefined
            }
            endDate={
              initialValues.endDate
                ? new Date(initialValues.endDate)
                : undefined
            }
            closedDays={closedDays}
          />
          <ExperienceBulkEditValues />
        </div>
        <ExperienceBulkEditFooter handleClose={handleClose} form={form} />
      </Form>
      {!isLoading && data && currentRange && (
        <ValidationExperienceBulkEditModal
          isOpened={isValidationBulkEditModalOpen}
          setIsValidationBulkEditModalOpen={setIsValidationBulkEditModalOpen}
          hotelId={hotelId}
          payload={payload}
          experienceOpeningDays={data}
          handleClose={handleClose}
          mustHavePackNames={mustHavePackNames}
        />
      )}
    </Drawer>
  );
};
